import { logger } from '@inboundx/shared/platform';
import { ChevronRight, Pin } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { OnboardingSessionReplay } from '@/analytics/sessionReplay';
import { VisualFeedbackWidget } from '@/components/feedback/VisualFeedbackWidget';
import { KbIngestCard } from '@/components/setup/KbIngestCard';
import { StaffBadge } from '@/components/ui/StaffBadge';
import { SidebarProvider, SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { usePinnedPages } from '@/contexts/PinnedPagesContext';
import { useOptionalWorkspace } from '@/contexts/WorkspaceContext';
import { useSetupProgress } from '@/hooks/useSetupProgress';
import { isPathWorkspace } from '@/services/workspaces';
import { AuthButton } from '../auth/AuthButton';
import { NoticeBanner } from './NoticeBanner';
import { PageContainer } from './PageContainer';
import { PathWorkspaceNotice } from './PathWorkspaceNotice';
import { type Crumb, findBodyH1Titles, usePageChrome } from './pageChrome';
import { Sidebar } from './Sidebar';
import { StudioSidebar } from './StudioSidebar';
import { WhatsNewBell } from './WhatsNewBell';
import { WorkspaceSelector } from './WorkspaceSelector';
import { WorkspaceSwitchOverlay } from './WorkspaceSwitchOverlay';

/** Route prefixes that live inside Config Studio (agents + shared parts). */
const STUDIO_PREFIXES = [
    '/config-studio',
    // Legacy URLs remain studio-scoped until their redirects render.
    '/website-agent',
    '/content-agent',
    '/agents',
    '/knowledge',
    '/integrations',
    '/health',
    // A config surface at a top-level path of its own (routeMeta parents it to
    // /config-studio), so it takes the same guard and the same sidebar. Not yet in
    // isPathWorkspaceConfigRoute: its hostname-keyed reads have not been reviewed.
    '/chatgpt-agent',
];

function isStudioRoute(pathname: string): boolean {
    return STUDIO_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Path workspaces may open configuration screens keyed by their config identity
 * (IX-5071), plus workspace-owned knowledge screens (IX-5070).
 * Keep exact knowledge routes: other studio surfaces still need ownership review.
 */
const PATH_WORKSPACE_CONFIG_ROUTES = new Set([
    '/config-studio',
    '/config-studio/brand',
    '/config-studio/guidelines',
    '/config-studio/sales-guidelines',
    '/config-studio/guidelines/competitors',
    '/config-studio/website-agent',
    '/config-studio/data-sources',
    '/config-studio/data-sources/faq',
    '/config-studio/data-sources/documents',
    '/config-studio/data-sources/website',
    '/config-studio/sales-guidelines/pricing',
    '/config-studio/guidelines/pricing',
    '/config-studio/knowledge',
    '/config-studio/knowledge/website-sync',
    '/config-studio/knowledge/pricing',
    '/knowledge',
    // GEO pages are owned per workspace (IX-5504). The domain page hides the custom
    // subdomain for a path workspace: its pointers are keyed by the registered domain.
    '/config-studio/content-agent',
    '/config-studio/content-agent/observatory',
    '/config-studio/content-agent/setup',
    '/config-studio/content-agent/domain',
]);
const WEBSITE_AGENT_HOSTNAME_KEYED = new Set([
    // Dataset runs send only a hostname, so they would grade the host's agent (IX-5069).
    'evaluations',
    'publish',
    'ai-sections',
    'curated-content',
    'ab-test',
    // Formerly the ChatGPT Agent page, refused for path workspaces (IX-5910).
    'chatgpt',
]);

export function isPathWorkspaceConfigRoute(pathname: string): boolean {
    const path = pathname.replace(/\/+$/, '') || '/';
    if (PATH_WORKSPACE_CONFIG_ROUTES.has(path)) return true;
    // Starter questions, calls-to-action and the schema-driven settings screens.
    const setting = /^\/config-studio\/website-agent\/([^/]+)$/.exec(path)?.[1];
    return setting !== undefined && !WEBSITE_AGENT_HOSTNAME_KEYED.has(setting);
}

// Below this width the sidebar and the full-bleed Playground can't both fit, so
// the sidebar auto-collapses to its icon rail. Above the mobile breakpoint (768),
// where the sidebar is a fixed rail rather than an off-canvas sheet.
const SIDEBAR_MIN_ROOM = 1280;

function useNarrowViewport(): boolean {
    const query = `(max-width: ${SIDEBAR_MIN_ROOM - 1}px)`;
    const [narrow, setNarrow] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(query).matches
    );
    useEffect(() => {
        const mql = window.matchMedia(query);
        const onChange = () => setNarrow(mql.matches);
        onChange();
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, [query]);
    return narrow;
}

/**
 * Drives the sidebar's collapsed state from context signals, without ever
 * stomping a manual toggle: each effect is one-directional and gated on a real
 * transition (a ref), so re-runs from `setOpen`'s changing identity are no-ops.
 */
function SidebarAutoBehavior() {
    const { setOpen } = useSidebar();
    const { isLive, isLiveLoading } = useSetupProgress();
    const isNarrow = useNarrowViewport();

    // IX-4936: fold when the window becomes too narrow to show the Playground
    // beside the sidebar. Only folds — widening again leaves the user's choice.
    const wasNarrow = useRef(false);
    useEffect(() => {
        if (isNarrow && !wasNarrow.current) setOpen(false);
        wasNarrow.current = isNarrow;
    }, [isNarrow, setOpen]);

    // IX-4937: fold while onboarding is in progress, unfold once the site goes
    // live. Acts on the first resolve and on the go-live transition only.
    const wasLive = useRef<boolean | null>(null);
    useEffect(() => {
        if (isLiveLoading) return;
        if (wasLive.current === null) {
            if (!isLive) setOpen(false);
        } else if (!wasLive.current && isLive) {
            setOpen(true);
        }
        wasLive.current = isLive;
    }, [isLive, isLiveLoading, setOpen]);

    return null;
}

/** Header toggle that pins/unpins the current page (path + query). */
function PinToggle() {
    const location = useLocation();
    const chrome = usePageChrome();
    const { isPinned, pin, unpin } = usePinnedPages();

    // Home is always one click away in the sidebar — nothing to pin.
    if (location.pathname === '/') return null;

    const currentPath = location.pathname + location.search;
    const pinned = isPinned(currentPath);
    // Label the pin with the active page's title (registered by <Page>).
    const label = chrome?.title ?? location.pathname;

    return (
        <button
            type="button"
            aria-pressed={pinned}
            aria-label={pinned ? 'Unpin this page' : 'Pin this page'}
            title={pinned ? 'Unpin this page' : 'Pin this page'}
            onClick={() => (pinned ? unpin(currentPath) : pin(currentPath, label))}
            className="flex h-7 w-7 items-center justify-center rounded-md text-foreground-subtle transition-colors hover:bg-sidebar-accent hover:text-foreground aria-pressed:text-[var(--accent-foreground)]"
        >
            <Pin className={`h-4 w-4 ${pinned ? 'fill-current' : ''}`} />
        </button>
    );
}

interface AppLayoutProps {
    children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
    const location = useLocation();
    const chrome = usePageChrome();
    // Optional: the layout also renders where no workspace is selected yet.
    const selectedWorkspace = useOptionalWorkspace()?.selectedWorkspace ?? null;
    const pathWorkspace = selectedWorkspace !== null && isPathWorkspace(selectedWorkspace);
    const studioBlocked =
        selectedWorkspace !== null &&
        pathWorkspace &&
        isStudioRoute(location.pathname) &&
        !isPathWorkspaceConfigRoute(location.pathname);
    // The active <Page> owns the trail: its parent breadcrumbs plus the title as
    // the leaf. Pages register this via the PageChrome context.
    const breadcrumbs: Crumb[] = chrome
        ? [...(chrome.breadcrumbs ?? []), { label: chrome.title }]
        : [];

    // The page <h1> is the breadcrumb leaf below, rendered once here. A page that
    // also writes one in its body ships the duplicate title IX-4244 reported, and
    // it reads as an ordinary heading, so nothing catches it. Fail loudly in dev
    // instead of relying on the convention being remembered.
    const mainRef = useRef<HTMLElement>(null);
    useEffect(() => {
        if (!import.meta.env.DEV) return;
        const offenders = findBodyH1Titles(mainRef.current);
        if (offenders.length > 0) {
            logger.error(
                `[AppLayout] ${location.pathname} renders <h1> in the page body: ` +
                    `${offenders.map((t) => `"${t}"`).join(', ')}. ` +
                    'The page <h1> is the <Page title> breadcrumb leaf in the top bar — ' +
                    'body headings start at <h2>.'
            );
        }
    }, [location.pathname]);

    return (
        <SidebarProvider>
            <SidebarAutoBehavior />
            <OnboardingSessionReplay />
            {isStudioRoute(location.pathname) ? <StudioSidebar /> : <Sidebar />}

            {/* Main content */}
            <div className="flex max-h-svh flex-1 flex-col overflow-hidden">
                {/* Outage only: a solid bar the whole app visibly sits below */}
                <NoticeBanner placement="bar" />

                {/* Top bar */}
                <header className="sticky top-0 z-5 h-16 shrink-0 border-b border-border bg-white">
                    <SidebarTrigger
                        className="absolute top-1/2 left-2 size-8 -translate-y-1/2"
                        aria-label="Toggle sidebar"
                        title="Toggle sidebar"
                    />
                    <PageContainer className="flex h-full items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            {breadcrumbs.length > 0 && (
                                <nav className="flex items-center gap-1.5">
                                    {breadcrumbs.map((crumb, i) => (
                                        <span
                                            key={crumb.href ?? crumb.label}
                                            className="flex items-center gap-1.5"
                                        >
                                            {i > 0 && (
                                                <ChevronRight className="h-4 w-4 text-foreground-subtle" />
                                            )}
                                            {crumb.href ? (
                                                <Link
                                                    to={crumb.href}
                                                    className="text-lg font-normal tracking-tight text-muted-foreground hover:text-[var(--accent-foreground)]"
                                                >
                                                    {crumb.label}
                                                </Link>
                                            ) : (
                                                <h1 className="text-lg font-semibold tracking-tight text-foreground">
                                                    {crumb.label}
                                                </h1>
                                            )}
                                        </span>
                                    ))}
                                    <PinToggle />
                                    {chrome?.staff && <StaffBadge />}
                                </nav>
                            )}
                        </div>
                        <div className="flex items-center gap-4">
                            <VisualFeedbackWidget />
                            <WhatsNewBell />
                            <WorkspaceSelector />
                            <AuthButton />
                        </div>
                    </PageContainer>
                </header>

                {/* The Knowledge hub renders the full card in its content. The
                    compact platform banner belongs only on the Playground. */}
                {location.pathname.replace(/\/$/, '') ===
                    '/config-studio/website-agent/playground' && (
                    <KbIngestCard variant="platform" />
                )}

                {/* Page content */}
                {/* flex-col so a bottom-docked child (ConfigSaveBar) can `mt-auto`
                    onto the bottom edge when the page is shorter than the screen. */}
                <main ref={mainRef} className="relative flex flex-1 flex-col overflow-auto">
                    <WorkspaceSwitchOverlay />
                    {/* Every other level: an in-content strip that inherits the
                        page gutter, so it never fights a page's own header. */}
                    <PageContainer
                        asChild
                        layout={chrome?.variant === 'canvas' ? 'full' : 'centered'}
                        className={chrome?.variant === 'canvas' ? 'pt-4' : 'pt-8'}
                    >
                        <NoticeBanner placement="strip" />
                    </PageContainer>
                    {/* shrink-0: <main> is a flex column, so without it a page
                        taller than the screen would be squeezed to fit instead of
                        scrolling. */}
                    {/* Keyed by workspace: two workspaces on one host share selectedDomain,
                        which screens use as the identity of their form state. Without a
                        remount, edits started in one would be saved into the other. */}
                    <PageContainer
                        key={selectedWorkspace?.id ?? 'no-workspace'}
                        layout={chrome?.variant === 'canvas' ? 'bleed' : 'centered'}
                        className={
                            chrome?.variant === 'canvas' ? 'h-full shrink-0' : 'shrink-0 py-8'
                        }
                    >
                        {studioBlocked ? <PathWorkspaceNotice /> : children}
                    </PageContainer>
                </main>
            </div>
        </SidebarProvider>
    );
}
